import React, { useContext, useState, useEffect, useCallback } from 'react'
import { IPedido } from '../types/pedido'
import { getPedidos, getTiposRetiro } from '../components/services/pedidos.services'
import { TenantsContext } from '../../../context/tenants.context'
import { INITIAL_ROWS_PER_PAGE } from '../../Products/components/TableDataPagination'

export interface Filtros {
  pagina: number
  pageSize: number
  tipo: string
  codLocal: string
  envioDesde: any
  envioHasta: any
  idTipoRetiro: number
  codigoPedido: string
  archivados: boolean
}

const defaultFiltros: Filtros = {
  pagina: 0,
  pageSize: INITIAL_ROWS_PER_PAGE,
  tipo: '',
  codLocal: null,
  envioDesde: '',
  envioHasta: '',
  idTipoRetiro: 0,
  codigoPedido: '',
  archivados: false
}

export interface PaginatorRes {
  pageNumber: number
  pageSize: number
  hasNextPage: boolean
  totalRecord: number
  handleChangePage: (e: any, newPage: number) => void
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export const defaultPaginatorRes: PaginatorRes = {
  pageNumber: 0,
  pageSize: 10,
  hasNextPage: false,
  totalRecord: 0,
  handleChangePage: () => {},
  handleChangeRowsPerPage: () => {}
}

interface PedidosContextProps {
  pedidos: IPedido[]
  setLoading: (loading: boolean) => void
  loading: boolean
  searching: boolean
  archivados: boolean
  setArchivados: (archivados: boolean) => void
  tiposDeRetiro: any
  setSearching: (searching: boolean) => void
  setFiltros: (filtros: Filtros) => void
  PaginatorRes: PaginatorRes
  filtros: Filtros
}

// create context
export const PedidosContext = React.createContext<PedidosContextProps>({
  pedidos: [],
  setLoading: () => {},
  loading: false,
  searching: false,
  archivados: false,
  setArchivados: () => {},
  tiposDeRetiro: [],
  setSearching: () => {},
  setFiltros: () => {},
  PaginatorRes: Object.assign({}, defaultPaginatorRes),
  filtros: defaultFiltros
})

// create provider
export const PedidosProvider = ({ children }: any) => {
  const { tenant } = useContext(TenantsContext)
  const [pedidos, setPedidos] = useState<IPedido[]>([])
  const [tiposDeRetiro, setTiposDeRetiro] = useState<any[]>([])

  const [loading, setLoading] = useState<boolean>(false)
  const [searching, setSearching] = useState<boolean>(false)
  const [archivados, setArchivados] = useState<boolean>(false)
  const [filtros, setFiltros] = useState<Filtros>(defaultFiltros)
  const [PaginatorRes, setPaginatorRes] = useState<PaginatorRes>(defaultPaginatorRes)

  useEffect(() => {
    async function fetchTiposDeRetiro() {
      if (tenant.path) {
        const tiposRetiro = await getTiposRetiro(tenant)
        setTiposDeRetiro(tiposRetiro)
      }
    }

    fetchTiposDeRetiro()
  }, [])

  const fetchData = useCallback(async () => {
    if (tenant.path) {
      const response = await getPedidos(filtros, tenant).then((res) => res.data)

      setPaginatorRes({
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        hasNextPage: response.hasNextPage,
        totalRecord: response.totalRecord,
        handleChangePage: PaginatorRes.handleChangePage,
        handleChangeRowsPerPage: PaginatorRes.handleChangeRowsPerPage
      })
      setPedidos(response.data)
    }
  }, [filtros])

  PaginatorRes.handleChangePage = (e: any, newPage: number) => {
    setFiltros((filtros) => ({
      ...filtros,
      pagina: newPage
    }))
  }

  PaginatorRes.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFiltros((filtros) => ({
      ...filtros,
      pageSize: parseInt(event.target.value, 10),
      pagina: 0
    }))
  };

  useEffect(() => {
    if (!searching) return

    setLoading(true)
    Promise.all([fetchData()])
      .catch(console.log)
      .finally(() => {
        setLoading(false)
      })
  }, [filtros, searching])

  if (!pedidos || !tiposDeRetiro) return null

  return (
    <PedidosContext.Provider
      value={{
        pedidos,
        setLoading,
        loading,
        archivados,
        setArchivados,
        setFiltros,
        filtros,
        PaginatorRes,
        searching,
        tiposDeRetiro,
        setSearching
      }}
    >
      {children}
    </PedidosContext.Provider>
  )
}
